import React, { useState } from 'react';
import { X, FolderPlus } from 'lucide-react';

interface CreateFolderModalProps {
    isOpen: boolean;
    currentPath: string;
    onClose: () => void;
    onCreate: (folderName: string) => void;
}

const CreateFolderModal: React.FC<CreateFolderModalProps> = ({
    isOpen,
    currentPath,
    onClose,
    onCreate
}) => {
    const [folderName, setFolderName] = useState('');
    const [error, setError] = useState('');

    const handleCreate = () => {
        if (!folderName.trim()) {
            setError('Folder name cannot be empty');
            return;
        }
        if (folderName.includes('/') || folderName.includes('\\')) {
            setError('Folder name cannot contain slashes');
            return;
        }
        onCreate(folderName.trim());
        setFolderName('');
        setError('');
        onClose();
    };

    const handleClose = () => {
        setFolderName('');
        setError('');
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div className="fixed inset-0 z-50 flex items-center justify-center">
            {/* Backdrop */}
            <div
                className="absolute inset-0 bg-black/50"
                onClick={handleClose}
            />

            {/* Modal */}
            <div className="relative bg-white rounded-xl shadow-xl w-full max-w-md mx-4">
                {/* Header */}
                <div className="flex items-center justify-between p-4 border-b border-gray-200">
                    <div className="flex items-center gap-3">
                        <div className="p-2 bg-amber-100 rounded-lg">
                            <FolderPlus size={20} className="text-amber-600" />
                        </div>
                        <h3 className="text-lg font-semibold text-gray-900">Create New Folder</h3>
                    </div>
                    <button
                        onClick={handleClose}
                        className="p-1 hover:bg-gray-100 rounded-lg transition-colors"
                    >
                        <X size={20} className="text-gray-500" />
                    </button>
                </div>

                {/* Content */}
                <div className="p-4">
                    <div className="mb-4">
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                            Current Location
                        </label>
                        <p className="text-sm text-gray-600 bg-gray-50 px-3 py-2 rounded-lg">
                            {currentPath || '/'}
                        </p>
                    </div>
                    <div>
                        <label className="block text-sm font-medium text-gray-700 mb-2">
                            Folder Name
                        </label>
                        <input
                            type="text"
                            value={folderName}
                            onChange={(e) => {
                                setFolderName(e.target.value);
                                setError('');
                            }}
                            placeholder="New Folder"
                            className="w-full px-4 py-2.5 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-amber-500 focus:border-amber-500"
                            onKeyDown={(e) => e.key === 'Enter' && handleCreate()}
                        />
                        {error && (
                            <p className="mt-2 text-sm text-red-600">{error}</p>
                        )}
                    </div>
                </div>

                {/* Footer */}
                <div className="flex justify-end gap-3 p-4 border-t border-gray-200">
                    <button
                        onClick={handleClose}
                        className="px-4 py-2 border border-gray-300 rounded-lg text-sm hover:bg-gray-50 transition-colors"
                    >
                        Cancel
                    </button>
                    <button
                        onClick={handleCreate}
                        className="px-4 py-2 bg-amber-600 text-white rounded-lg text-sm hover:bg-amber-700 transition-colors"
                    >
                        Create Folder
                    </button>
                </div>
            </div>
        </div>
    );
};

export default CreateFolderModal;
